import type { LanguageCode } from "@/components/translations";
import type { ScheduleDay, ScheduleNotificationSettingsValue } from "@/components/schedule";
import type { Module } from "../modules/types";


/**
 * Course translation
 */
export interface CourseTranslation {
  id: string;
  courseId: string;
  languageCode: LanguageCode;
  title: string;
  description: string | null;
  shortDescription: string | null;
  location: string | null;
  venue: string | null;
}

/**
 * Teacher/Instructor (simplified for select)
 */
export interface InstructorSelect {
  id: string;
  name?: string;
  photoUrl: string | null;
  translations?: { languageCode: string; name: string }[];
}

export interface CoursePricingTier {
  id: number;
  prices: Record<string, number>;
  baseCurrency: string;
  isActive: boolean;
  order: number;
}

export type CourseType = "ONLINE" | "MEETING" | "OFFLINE" | "WEBINAR";

/**
 * Schedule item for ONLINE/MEETING courses
 */
export interface CourseScheduleItem {
  date: string;
  title: string;
  description?: string;
}

/**
 * Course entity
 */
export interface Course {
  id: string;
  slug: string;
  durationMinutes: number | null;
  lessonsCount: number;
  thumbnailUrl: string | null;
  thumbnailId: number | null;
  bannerImageId: number | null;
  bannerImageUrl: string | null;
  pastImageId: number | null;
  pastImageUrl: string | null;
  previewVideoUrl: string | null;
  order: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  /** @deprecated Используй instructors (many-to-many). Оставлено для обратной совместимости. */
  instructorId: string | null;
  // Новые поля для типов курсов
  courseType: CourseType;
  startDate: string | null;
  endDate: string | null;
  courseHoursFrom: string | null;
  courseHoursTo: string | null;
  accessDurationDays: number | null;
  schedule?: ScheduleDay[];
  scheduleTimezone?: string | null;
  scheduleNotificationSettings?: ScheduleNotificationSettingsValue | null;
  // Встречи
  capacity: number | null;
  bookedSeats: number | null;
  // Ценообразование
  isPurchasable: boolean;
  price: number | null;
  currency: string;
  prices: Record<string, unknown> | null;
  baseCurrency: string;
  pricingTiers?: CoursePricingTier[];
  // Языковая аудитория: [] — виден всем, ['ru'] — только ru, ['ru','uz'] — ru или uz
  targetLanguages: string[];
  // Triggers
  onAccessExpiredTriggerId?: number | string | null;
  onCourseCompletedTriggerId?: number | string | null;
  // Relations
  translations?: CourseTranslation[];
  /** @deprecated Используй instructors. Оставлено для обратной совместимости. */
  instructor: InstructorSelect | null;
  /** Преподаватели курса (many-to-many) */
  instructors?: InstructorSelect[];
  modules?: Module[];
}

/**
 * Course for list view (with computed fields)
 */
export interface CourseListItem extends Course {
  studentsCount?: number;
  modulesCount?: number;
}

/**
 * Course create/update payload
 */
export interface CourseFormData {
  slug: string;
  durationMinutes: number | null;
  thumbnailUrl: string | null;
  thumbnailId: number | null;
  bannerImageId: number | null;
  pastImageId: number | null;
  previewVideoUrl: string | null;
  isPublished: boolean;
  /** @deprecated Используй instructorIds. Оставлено для обратной совместимости. */
  instructorId?: string | number | null;
  /** Массив ID преподавателей (many-to-many) */
  instructorIds?: number[];
  courseType: CourseType;
  startDate: string | null;
  endDate: string | null;
  courseHoursFrom: string | null;
  courseHoursTo: string | null;
  accessDurationDays: number | null;
  schedule?: ScheduleDay[];
  scheduleTimezone?: string;
  scheduleNotificationSettings?: ScheduleNotificationSettingsValue | null;
  order?: number;
  onAccessExpiredTriggerId?: number | null;
  onCourseCompletedTriggerId?: number | null;
  // Встречи
  capacity?: number | null;
  // Ценообразование (isPurchasable вычисляется автоматически на бэкенде: price > 0 = платный)
  // Опциональные — ценообразование управляется через таб "Тарифы" (PricingTiersSection)
  price?: number | null;
  currency?: string;
  prices?: Record<string, unknown> | null;
  baseCurrency?: string;
  // Языковая аудитория
  targetLanguages?: string[];
  translations: {
    languageCode: LanguageCode;
    title: string;
    description: string | null;
    shortDescription: string | null;
    location: string | null;
    venue: string | null;
  }[];
}

/**
 * Get translated title for course
 */
export function getCourseTitle(
  course: Course,
  languageCode: LanguageCode = "ru"
): string {
  const translation = course.translations?.find(
    (t) => t.languageCode === languageCode
  );
  return translation?.title ?? course.translations?.[0]?.title ?? course.slug;
}
